import { AudioLines, Loader2, Mic, MicOff, Square } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { VoiceMode } from "../../lib/voice-mode";
import {
  type VoiceTrainingClient,
  type VoiceTrainingEvent,
  connectVoiceTraining,
} from "../../lib/voice-training";
import { Button } from "../ui/button";
import { VoiceSessionBanner } from "./VoiceSessionBanner";
import { WaveformVisualizer } from "./WaveformVisualizer";
import { useVad } from "./useVad";

const OUTPUT_SAMPLE_RATE = 24000;
// Silence (ms) that ends an utterance in sandwich mode, triggering an
// `input_audio_buffer.commit`. Long enough to ride over natural pauses, short
// enough to keep the turn responsive.
const SANDWICH_SILENCE_MS = 800;
// While the assistant is speaking, the mic still picks up the TTS echo that
// survives the browser's AEC (weak with headphones, louder with speakers) and
// can trip the VAD into committing — cutting the assistant off mid-answer. Only
// treat detected speech as a real barge-in when the mic RMS (0..1) clears this
// level; real user speech close to the mic does, residual echo does not.
// Tunable — raise it if the assistant still cuts itself off over speakers.
const ECHO_BARGE_IN_LEVEL = 0.12;
// Silero VAD is the primary detector. This lightweight RMS fallback keeps the
// sandwich path responsive if the model misses an utterance or fails to emit
// speech-end: the server receives a commit after clear post-speech silence.
const FALLBACK_SPEECH_LEVEL = 0.018;
const FALLBACK_SILENCE_LEVEL = 0.01;
// User-controlled TTS playback speed (sandwich). Cycled from the banner,
// persisted in localStorage, and sent to the relay over WS — the backend
// applies it as a pitch-preserving TTS speed so the voice stays natural at any
// rate (client-side playbackRate would resample the pitch up — "chipmunk").
const TTS_SPEED_STEPS = [1, 1.25, 1.5, 1.75, 2] as const;
const TTS_SPEED_STORAGE_KEY = "beflow:tts-speed";

function readStoredTtsSpeed(): number {
  try {
    const parsed = Number.parseFloat(window.localStorage.getItem(TTS_SPEED_STORAGE_KEY) ?? "");
    return (TTS_SPEED_STEPS as readonly number[]).includes(parsed) ? parsed : 1;
  } catch {
    return 1;
  }
}
// How long paused playback waits for the relay's verdict (resume/stop or a
// response cancellation) before resuming on its own. Covers VAD end-of-speech
// plus transcription latency; without it a lost verdict would mute the
// assistant forever.
const PAUSE_VERDICT_TIMEOUT_MS = 4000;

export type VoiceTrainingButtonState = "idle" | "loading" | "listening" | "error";

const ARIA_LABELS: Record<VoiceTrainingButtonState | "unavailable", string> = {
  idle: "Iniciar entrenamiento por voz",
  loading: "Cargando entrenamiento por voz",
  listening: "Detener entrenamiento por voz",
  error: "Entrenamiento por voz no disponible",
  unavailable: "Entrenamiento por voz no disponible",
};

const MUTE_LABEL = "Silenciar microfono";
const UNMUTE_LABEL = "Activar microfono";

interface VoiceTrainingButtonProps {
  sessionId: string | null;
  /** Active global voice mode. `sandwich` drives a client-side VAD that commits
   * each utterance; `e2e` (or undefined) uses the provider's turn detection. */
  voiceMode?: VoiceMode | undefined;
  onVoiceEvent: (event: VoiceTrainingEvent) => void;
  disabled?: boolean;
  /** Reports whether a voice-training session is active (connecting or
   * listening) so the composer can disable features that compete for the
   * microphone (dictation). */
  onActiveChange?: (active: boolean) => void;
  /** Portal target (rendered sticky at the top of the chat by chat.tsx) where
   * the active-session banner is mounted. When null the banner is not shown
   * and the composer control alone reflects the active state. */
  bannerSlot?: HTMLElement | null;
}

export function VoiceTrainingButton({
  sessionId,
  voiceMode,
  onVoiceEvent,
  disabled,
  onActiveChange,
  bannerSlot,
}: VoiceTrainingButtonProps) {
  const [state, setState] = useState<VoiceTrainingButtonState>("idle");
  const [muted, setMuted] = useState(false);
  // Seconds left before idle auto-stop while the relay's idle warning is
  // active; null when there is no warning showing.
  const [idleSeconds, setIdleSeconds] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  // Which side is currently producing sound, so the banner waveform reads the
  // right analyser (the mic goes flat while the assistant speaks — its audio
  // is echo-cancelled out of the capture). "assistant" while playback is
  // scheduled, "user" otherwise.
  const [speaking, setSpeaking] = useState<"user" | "assistant">("user");
  // True while a proactive retrieval is in flight (drives the "Buscando…"
  // banner label); set from search_status events.
  const [searching, setSearching] = useState(false);
  // True from the instant a sandwich utterance is committed until a richer
  // signal arrives (search / transcript / audio). Drives an immediate
  // "Procesando…" banner so the post-commit STT+chat latency is not a silent
  // gap where nothing visibly happens.
  const [processing, setProcessing] = useState(false);
  // User-controlled TTS playback speed (sandwich); persisted across sessions.
  const [ttsSpeed, setTtsSpeed] = useState<number>(readStoredTtsSpeed);
  // Elapsed session seconds, shown in the banner. Reset on start, ticked while
  // listening.
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // Analyser tapped off the assistant playback graph, surfaced to the banner
  // waveform and the ambient-accent amplitude loop. The mic analyser is owned
  // by the WaveformVisualizer (stream + audioContext path); this one we own.
  const [outputAnalyser, setOutputAnalyser] = useState<AnalyserNode | null>(null);
  const clientRef = useRef<VoiceTrainingClient | null>(null);
  // The chat session id the active voice session was started for, so it can be
  // ended when the user switches chats (the sessionId prop changes).
  const startedSessionIdRef = useRef<string | null>(null);
  const startAbortRef = useRef<AbortController | null>(null);
  const startGenerationRef = useRef(0);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const outputAudioContextRef = useRef<AudioContext | null>(null);
  const outputAnalyserRef = useRef<AnalyserNode | null>(null);
  const micAnalyserRef = useRef<AnalyserNode | null>(null);
  const outputScheduledAtRef = useRef(0);
  // AudioContext time when the current playback run started. Needed to
  // report how much audio the user actually HEARD on interruption —
  // `playedMs` counts scheduled audio, which runs ahead of real time.
  const playbackStartedAtRef = useRef<number | null>(null);
  const activeOutputSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const assistantPlaybackRef = useRef<{
    itemId: string | null;
    responseId: string | null;
    playedMs: number;
  }>({ itemId: null, responseId: null, playedMs: 0 });
  // True between pausing playback at speech start and the relay's verdict
  // (resume for noise/backchannels, stop or response cancellation for a real
  // interruption). While set, playback state must survive transcript events.
  const pausedForSpeechRef = useRef(false);
  const pauseVerdictTimerRef = useRef<number | null>(null);
  const onVoiceEventRef = useRef(onVoiceEvent);
  const spokeSinceCommitRef = useRef(false);
  const fallbackSpeechActiveRef = useRef(false);
  const fallbackSilenceStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    onVoiceEventRef.current = onVoiceEvent;
  }, [onVoiceEvent]);

  const onActiveChangeRef = useRef(onActiveChange);

  useEffect(() => {
    onActiveChangeRef.current = onActiveChange;
  }, [onActiveChange]);

  useEffect(() => {
    onActiveChangeRef.current?.(state === "loading" || state === "listening");
  }, [state]);

  const commitSandwichUtterance = useCallback(() => {
    if (!spokeSinceCommitRef.current) return;
    spokeSinceCommitRef.current = false;
    fallbackSpeechActiveRef.current = false;
    fallbackSilenceStartedAtRef.current = null;
    clientRef.current?.sendCommit();
    // Immediate feedback: the turn is committed. Banner -> "Procesando..." and
    // the chat shows an "Analizando..." placeholder until transcript/audio.
    setProcessing(true);
    onVoiceEventRef.current({ kind: "user_speech_committed" });
  }, []);

  // Cascaded (sandwich) mode has no server-side turn detection, so a
  // client-side VAD delimits each utterance: on end-of-speech it commits the
  // input buffer, starting one orchestrator turn. `e2e` relies on the realtime
  // provider's own turn detection and never mounts the VAD (stream stays null).
  const useClientVad = voiceMode === "sandwich" && state === "listening";
  const { state: vadState } = useVad({
    stream: useClientVad ? stream : null,
    audioContext: useClientVad ? audioContext : null,
    silenceMs: SANDWICH_SILENCE_MS,
    onSilence: () => {
      // Ignore the VAD's initial silence arming (no speech yet) so silence
      // alone never commits an empty utterance.
      commitSandwichUtterance();
    },
  });
  // Mirror `speaking` into a ref so the VAD gate below can read it without
  // re-running on every playback flip.
  const speakingRef = useRef(speaking);
  useEffect(() => {
    speakingRef.current = speaking;
  }, [speaking]);
  useEffect(() => {
    if (vadState !== "speaking") return;
    // The VAD also fires on the assistant's own TTS echo while it plays. Gate
    // the barge-in by mic loudness: residual echo (post-AEC) stays below the
    // threshold and is ignored, so the assistant never cuts itself off; real
    // user speech clears it and still interrupts.
    if (
      speakingRef.current === "assistant" &&
      readAnalyserLevel(micAnalyserRef.current) < ECHO_BARGE_IN_LEVEL
    ) {
      return;
    }
    spokeSinceCommitRef.current = true;
  }, [vadState]);

  const clearPauseForSpeech = useCallback(() => {
    pausedForSpeechRef.current = false;
    if (pauseVerdictTimerRef.current !== null) {
      window.clearTimeout(pauseVerdictTimerRef.current);
      pauseVerdictTimerRef.current = null;
    }
  }, []);

  const stopAssistantPlayback = useCallback(() => {
    clearPauseForSpeech();
    for (const source of activeOutputSourcesRef.current) {
      try {
        source.stop(0);
      } catch {
        // already stopped
      }
    }
    activeOutputSourcesRef.current.clear();
    outputScheduledAtRef.current = 0;
    setSpeaking("user");
    const ctx = outputAudioContextRef.current;
    if (ctx && ctx.state === "running") {
      void ctx.suspend().then(() => {
        void ctx.resume();
      });
    } else if (ctx && ctx.state === "suspended") {
      // Stopped while paused for speech: leave the context running so the
      // next response's audio plays.
      void ctx.resume();
    }
  }, [clearPauseForSpeech]);

  const closeOutputAudio = useCallback(() => {
    stopAssistantPlayback();
    if (outputAudioContextRef.current) {
      void outputAudioContextRef.current.close();
    }
    outputAudioContextRef.current = null;
    outputAnalyserRef.current = null;
    setOutputAnalyser(null);
    assistantPlaybackRef.current = { itemId: null, responseId: null, playedMs: 0 };
    playbackStartedAtRef.current = null;
  }, [stopAssistantPlayback]);

  const interruptAssistantPlayback = useCallback(() => {
    const client = clientRef.current;
    const { itemId, playedMs } = assistantPlaybackRef.current;
    const hadActivePlayback = activeOutputSourcesRef.current.size > 0;
    const ctx = outputAudioContextRef.current;
    const startedAt = playbackStartedAtRef.current;
    // Truncate at what the user actually heard, not at the scheduled total:
    // chunks arrive faster than real time, so `playedMs` overshoots.
    const heardMs =
      ctx !== null && startedAt !== null
        ? Math.max(0, (ctx.currentTime - startedAt) * 1000)
        : playedMs;
    stopAssistantPlayback();
    if (client && itemId && hadActivePlayback) {
      client.interruptAssistant({
        itemId,
        audioEndMs: Math.max(0, Math.floor(Math.min(playedMs, heardMs))),
      });
    }
    assistantPlaybackRef.current = { itemId: null, responseId: null, playedMs: 0 };
    playbackStartedAtRef.current = null;
  }, [stopAssistantPlayback]);

  // Speech start no longer interrupts: it PAUSES playback (buffers and the
  // heard-position stay intact — `ctx.currentTime` freezes on suspend) until
  // the relay decides whether the utterance was a real interruption or just
  // noise/a backchannel.
  const resumeAssistantPlayback = useCallback(() => {
    const wasPaused = pausedForSpeechRef.current;
    clearPauseForSpeech();
    if (!wasPaused) return;
    const ctx = outputAudioContextRef.current;
    if (ctx && ctx.state === "suspended") {
      void ctx.resume();
    }
    // The interruption was noise/a backchannel and playback continues, so the
    // banner returns to the assistant side if audio is still scheduled.
    if (activeOutputSourcesRef.current.size > 0) {
      setSpeaking("assistant");
    }
  }, [clearPauseForSpeech]);

  const pauseAssistantPlayback = useCallback(() => {
    const ctx = outputAudioContextRef.current;
    if (!ctx || ctx.state !== "running" || activeOutputSourcesRef.current.size === 0) {
      return;
    }
    pausedForSpeechRef.current = true;
    void ctx.suspend();
    if (pauseVerdictTimerRef.current !== null) {
      window.clearTimeout(pauseVerdictTimerRef.current);
    }
    pauseVerdictTimerRef.current = window.setTimeout(() => {
      pauseVerdictTimerRef.current = null;
      resumeAssistantPlayback();
    }, PAUSE_VERDICT_TIMEOUT_MS);
  }, [resumeAssistantPlayback]);

  const rememberAssistantItem = useCallback((itemId?: string, responseId?: string) => {
    if (itemId) {
      assistantPlaybackRef.current.itemId = itemId;
    }
    if (responseId) {
      assistantPlaybackRef.current.responseId = responseId;
    }
  }, []);

  const playAssistantAudio = useCallback((base64Audio: string) => {
    if (!base64Audio) return;
    const ctx =
      outputAudioContextRef.current ?? new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
    outputAudioContextRef.current = ctx;
    // Tap an analyser between the playback sources and the speakers so the
    // banner waveform and the ambient accent can read the assistant's
    // amplitude. Created once per output context; passes audio through.
    let analyser = outputAnalyserRef.current;
    if (!analyser || analyser.context !== ctx) {
      analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.connect(ctx.destination);
      outputAnalyserRef.current = analyser;
      setOutputAnalyser(analyser);
    }
    const buffer = pcm16Base64ToAudioBuffer(base64Audio, ctx);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(analyser);
    const startAt = Math.max(ctx.currentTime, outputScheduledAtRef.current);
    if (activeOutputSourcesRef.current.size === 0) {
      playbackStartedAtRef.current = startAt;
    }
    source.start(startAt);
    outputScheduledAtRef.current = startAt + buffer.duration;
    assistantPlaybackRef.current.playedMs += buffer.duration * 1000;
    activeOutputSourcesRef.current.add(source);
    // The assistant is now speaking the answer; clear any lingering search
    // state so the banner never shows "Buscando…" over the spoken reply (a
    // terminal search_status may be dropped or arrive late).
    setSearching(false);
    setSpeaking("assistant");
    source.onended = () => {
      activeOutputSourcesRef.current.delete(source);
      if (activeOutputSourcesRef.current.size === 0) {
        outputScheduledAtRef.current = 0;
        assistantPlaybackRef.current = { itemId: null, responseId: null, playedMs: 0 };
        playbackStartedAtRef.current = null;
        setSpeaking("user");
      }
    };
  }, []);

  const disposeResources = useCallback(
    ({ closeClient, updateState }: { closeClient: boolean; updateState: boolean }) => {
      const client = clientRef.current;
      const source = sourceNodeRef.current;
      const worklet = workletNodeRef.current;
      clientRef.current = null;
      sourceNodeRef.current = null;
      workletNodeRef.current = null;
      if (worklet) {
        worklet.port.onmessage = null;
        worklet.disconnect();
      }
      if (source) {
        source.disconnect();
      }
      if (micAnalyserRef.current) {
        micAnalyserRef.current.disconnect();
        micAnalyserRef.current = null;
      }
      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) {
          track.stop();
        }
      }
      streamRef.current = null;
      setSpeaking("user");
      if (updateState) {
        setStream(null);
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close();
      }
      audioContextRef.current = null;
      if (updateState) {
        setAudioContext(null);
      }
      closeOutputAudio();
      if (closeClient && client) {
        client.close();
      }
    },
    [closeOutputAudio],
  );

  const stop = useCallback(() => {
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    setState("idle");
    setMuted(false);
    setIdleSeconds(null);
    setProcessing(false);
    disposeResources({ closeClient: true, updateState: true });
  }, [disposeResources]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => !prev);
  }, []);

  const cycleSpeed = useCallback(() => {
    setTtsSpeed((current) => {
      const idx = (TTS_SPEED_STEPS as readonly number[]).indexOf(current);
      return TTS_SPEED_STEPS[(idx + 1) % TTS_SPEED_STEPS.length] ?? 1;
    });
  }, []);
  // Persist the chosen speed and push it to the relay whenever it changes. The
  // backend applies it as a pitch-preserving TTS speed (per sentence), so the
  // voice stays natural at any rate (client-side playbackRate resampled the
  // pitch up — "chipmunk" — which is why this is server-side). Side effects
  // live here, not in the state updater, so StrictMode's double-invoke can't
  // send the WS message twice.
  useEffect(() => {
    try {
      window.localStorage.setItem(TTS_SPEED_STORAGE_KEY, String(ttsSpeed));
    } catch {
      // localStorage unavailable (e.g. private mode): keep it in-memory.
    }
    clientRef.current?.setTtsSpeed(ttsSpeed);
  }, [ttsSpeed]);

  const keepAlive = useCallback(() => {
    // Optimistically hide the countdown; the relay also emits a cleared event.
    setIdleSeconds(null);
    clientRef.current?.sendKeepalive();
  }, []);

  const observeFallbackVadFrame = useCallback(
    (pcm16Frame: ArrayBuffer) => {
      if (voiceMode !== "sandwich") return;
      const samples = new Int16Array(pcm16Frame);
      if (samples.length === 0) return;
      let sumSquares = 0;
      for (let i = 0; i < samples.length; i += 1) {
        const normalized = (samples[i] ?? 0) / 32768;
        sumSquares += normalized * normalized;
      }
      const level = Math.sqrt(sumSquares / samples.length);
      if (level >= FALLBACK_SPEECH_LEVEL) {
        if (
          speakingRef.current === "assistant" &&
          readAnalyserLevel(micAnalyserRef.current) < ECHO_BARGE_IN_LEVEL
        ) {
          return;
        }
        spokeSinceCommitRef.current = true;
        fallbackSpeechActiveRef.current = true;
        fallbackSilenceStartedAtRef.current = null;
        return;
      }
      if (!fallbackSpeechActiveRef.current || level > FALLBACK_SILENCE_LEVEL) return;
      const now = performance.now();
      const silenceStartedAt = fallbackSilenceStartedAtRef.current;
      if (silenceStartedAt === null) {
        fallbackSilenceStartedAtRef.current = now;
        return;
      }
      if (now - silenceStartedAt >= SANDWICH_SILENCE_MS) {
        commitSandwichUtterance();
      }
    },
    [commitSandwichUtterance, voiceMode],
  );

  // Tick the idle countdown down locally once the warning is showing. The
  // relay emits a single warning; a one-shot timer re-armed each second keeps
  // the displayed number live and stops on its own at 0 (the relay's
  // idle_timeout + close is the authoritative stop).
  useEffect(() => {
    if (idleSeconds === null || idleSeconds <= 0) return;
    const timer = window.setTimeout(() => {
      setIdleSeconds((seconds) => (seconds === null ? null : Math.max(0, seconds - 1)));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [idleSeconds]);

  // Ambient accent: while listening, write the live audio amplitude (the
  // louder of mic and assistant playback) to `--voice-amplitude` on :root each
  // animation frame. CSS in chat.tsx reads it to modulate the glow without any
  // React re-render per frame. Cleared on teardown.
  useEffect(() => {
    if (state !== "listening") return;
    const root = document.documentElement;
    let frame = 0;
    const tick = () => {
      const level = Math.min(
        1,
        Math.max(
          readAnalyserLevel(micAnalyserRef.current),
          readAnalyserLevel(outputAnalyserRef.current),
        ) * 3,
      );
      root.style.setProperty("--voice-amplitude", level.toFixed(3));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      root.style.removeProperty("--voice-amplitude");
    };
  }, [state]);

  // Elapsed session timer for the banner. Ticks once a second while listening;
  // `start()` resets it to 0.
  useEffect(() => {
    if (state !== "listening") return;
    const timer = window.setInterval(() => {
      setElapsedSeconds((seconds) => seconds + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [state]);

  // Leave "Procesando…" as soon as there's a richer signal: the assistant
  // started speaking or a proactive search kicked in.
  useEffect(() => {
    if (speaking === "assistant" || searching) setProcessing(false);
  }, [speaking, searching]);
  // Fallback only for a committed turn that yields no response events at all
  // (e.g. empty STT): clear "Procesando…" after a window long enough to outlast
  // a real turn — STT + RAG + first-sentence TTS can take ~30 s — so the
  // indicator never disappears mid-processing, only on a genuinely dead turn.
  useEffect(() => {
    if (!processing) return;
    const timer = window.setTimeout(() => setProcessing(false), 60000);
    return () => window.clearTimeout(timer);
  }, [processing]);

  // Mute by disabling the capture tracks: a disabled track produces silence
  // while audio frames keep flowing to the relay, so the server watchdog
  // stays fed and any in-flight search/answer continues uninterrupted.
  useEffect(() => {
    if (!stream) return;
    for (const track of stream.getAudioTracks()) {
      track.enabled = !muted;
    }
  }, [muted, stream]);

  const handleClientEvent = useCallback(
    (event: VoiceTrainingEvent) => {
      if (event.kind === "open") {
        // Apply the persisted TTS speed once the socket is open — setTtsSpeed
        // is a no-op while CONNECTING, so it can't be sent right after connect.
        const speed = readStoredTtsSpeed();
        if (speed !== 1) clientRef.current?.setTtsSpeed(speed);
      } else if (event.kind === "speech_started") {
        // The user is talking now: reflect it in the banner (mic waveform +
        // "Escuchando…") while playback is paused awaiting the relay verdict.
        // resumeAssistantPlayback() flips back to "assistant" if it was noise.
        setSpeaking("user");
        pauseAssistantPlayback();
      } else if (event.kind === "search_status") {
        // "Buscando…" while a proactive retrieval runs; clear on any terminal
        // status (done / stale / error).
        setSearching(event.status === "searching");
      } else if (event.kind === "playback") {
        if (event.action === "resume") {
          resumeAssistantPlayback();
        } else {
          // Confirmed interruption with nothing generating server-side:
          // truncate at what was heard and drop the paused tail.
          interruptAssistantPlayback();
        }
      } else if (event.kind === "response_cancelled") {
        if (pausedForSpeechRef.current) {
          // Confirmed interruption of an active response: persist the
          // truncation before dropping the paused playback.
          interruptAssistantPlayback();
        } else {
          stopAssistantPlayback();
          assistantPlaybackRef.current = { itemId: null, responseId: null, playedMs: 0 };
          playbackStartedAtRef.current = null;
        }
      } else if (event.kind === "user_transcript_final") {
        // An empty final = the transcriber heard nothing usable, so no answer
        // is coming: drop "Procesando…" now instead of waiting out the timeout.
        if (event.text.trim().length === 0) setProcessing(false);
        if (!pausedForSpeechRef.current) {
          assistantPlaybackRef.current = { itemId: null, responseId: null, playedMs: 0 };
          playbackStartedAtRef.current = null;
        }
      } else if (
        event.kind === "assistant_transcript_delta" ||
        event.kind === "assistant_transcript_final" ||
        event.kind === "assistant_audio_delta"
      ) {
        rememberAssistantItem(event.itemId, event.responseId);
        if (event.kind === "assistant_audio_delta") {
          playAssistantAudio(event.audio);
        }
      } else if (event.kind === "idle_warning") {
        setIdleSeconds(event.secondsRemaining);
      } else if (event.kind === "idle_warning_cleared" || event.kind === "idle_timeout") {
        // idle_timeout is followed by a clean 1000 close that returns the
        // control to idle; just hide the countdown here.
        setIdleSeconds(null);
      } else if (event.kind === "error") {
        setIdleSeconds(null);
        setSearching(false);
        setErrorMessage(`No se pudo entrenar por voz (${event.code})`);
        setState("error");
      } else if (event.kind === "close") {
        setIdleSeconds(null);
        setSearching(false);
        disposeResources({ closeClient: false, updateState: true });
        setState((current) => (current === "error" ? "error" : "idle"));
      }
      onVoiceEventRef.current(event);
    },
    [
      disposeResources,
      interruptAssistantPlayback,
      pauseAssistantPlayback,
      playAssistantAudio,
      rememberAssistantItem,
      resumeAssistantPlayback,
      stopAssistantPlayback,
    ],
  );

  const start = useCallback(async () => {
    if (sessionId === null) return;
    startAbortRef.current?.abort();
    const generation = startGenerationRef.current + 1;
    startGenerationRef.current = generation;
    const abort = new AbortController();
    startAbortRef.current = abort;
    const isCurrent = (): boolean =>
      startGenerationRef.current === generation && !abort.signal.aborted;
    startedSessionIdRef.current = sessionId;
    setErrorMessage(null);
    setMuted(false);
    setSearching(false);
    setElapsedSeconds(0);
    setState("loading");
    if (
      typeof window.AudioWorkletNode === "undefined" ||
      typeof navigator.mediaDevices === "undefined"
    ) {
      setErrorMessage("Tu navegador no soporta entrenamiento por voz");
      setState("error");
      return;
    }

    let mediaStream: MediaStream;
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      if (!isCurrent()) return;
      setErrorMessage("Necesitamos permiso del microfono");
      setState("error");
      return;
    }
    if (!isCurrent()) {
      for (const track of mediaStream.getTracks()) track.stop();
      return;
    }

    const ctx = new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
    try {
      await ctx.audioWorklet.addModule("/audio-worklets/pcm16-downsampler.js");
    } catch {
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      if (!isCurrent()) return;
      setErrorMessage("No se pudo cargar el procesador de audio");
      setState("error");
      return;
    }
    if (!isCurrent()) {
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      return;
    }

    const source = ctx.createMediaStreamSource(mediaStream);
    const worklet = new AudioWorkletNode(ctx, "pcm16-downsampler");
    source.connect(worklet);
    // Mic analyser for the banner waveform (user side) and the ambient-accent
    // amplitude loop. Fans out off the same source as the worklet; not
    // connected to the destination so the mic is never played back.
    const micAnalyser = ctx.createAnalyser();
    micAnalyser.fftSize = 256;
    source.connect(micAnalyser);

    let client: VoiceTrainingClient;
    try {
      client = await connectVoiceTraining({
        sessionId,
        onEvent: (event) => {
          if (startGenerationRef.current !== generation) return;
          handleClientEvent(event);
        },
      });
    } catch {
      worklet.disconnect();
      source.disconnect();
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      if (!isCurrent()) return;
      setErrorMessage("No se pudo abrir la sesion de voz");
      setState("error");
      return;
    }
    if (!isCurrent()) {
      client.close();
      worklet.disconnect();
      source.disconnect();
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      return;
    }

    worklet.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      client.sendAudio(event.data);
      observeFallbackVadFrame(event.data);
    };
    sourceNodeRef.current = source;
    workletNodeRef.current = worklet;
    clientRef.current = client;
    micAnalyserRef.current = micAnalyser;
    streamRef.current = mediaStream;
    audioContextRef.current = ctx;
    setAudioContext(ctx);
    setStream(mediaStream);
    if (!isCurrent()) {
      client.close();
      worklet.disconnect();
      source.disconnect();
      for (const track of mediaStream.getTracks()) track.stop();
      await ctx.close();
      return;
    }
    setState("listening");
  }, [handleClientEvent, observeFallbackVadFrame, sessionId]);

  const handleClick = useCallback(() => {
    if (disabled || sessionId === null) return;
    if (state === "loading" || state === "listening") {
      stop();
      return;
    }
    void start();
  }, [disabled, sessionId, start, state, stop]);

  useEffect(() => {
    if (disabled && state === "listening") {
      stop();
    }
  }, [disabled, state, stop]);

  // End the voice session when the user switches chats: a session belongs to
  // the chat it started in, so a changed sessionId means we left that chat.
  // Without this the old session keeps streaming mic audio into the previous
  // chat and answers stray noise there.
  useEffect(() => {
    if (
      (state === "loading" || state === "listening") &&
      startedSessionIdRef.current !== null &&
      sessionId !== startedSessionIdRef.current
    ) {
      stop();
    }
  }, [sessionId, state, stop]);

  // Cleanup on unmount only — deps MUST stay empty so state updates inside
  // `start()` do not re-run cleanup and kill a still-handshaking WebSocket.
  useEffect(() => {
    return () => {
      startAbortRef.current?.abort();
      if (clientRef.current) {
        clientRef.current.close();
      }
      if (workletNodeRef.current) {
        workletNodeRef.current.port.onmessage = null;
        workletNodeRef.current.disconnect();
      }
      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect();
      }
      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) {
          track.stop();
        }
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close();
      }
      if (outputAudioContextRef.current) {
        void outputAudioContextRef.current.close();
      }
    };
  }, []);

  if (state === "listening" && stream && audioContext) {
    // With a banner slot wired (the chat page), every control of the active
    // session lives in the sticky banner — the composer renders nothing, so
    // stopping happens only from the banner's stop button.
    if (bannerSlot) {
      const statusLabel = searching
        ? "Buscando…"
        : speaking === "assistant"
          ? "Hablando…"
          : processing
            ? "Procesando…"
            : "Escuchando…";
      return createPortal(
        <VoiceSessionBanner
          statusLabel={statusLabel}
          busy={processing || searching}
          elapsedSeconds={elapsedSeconds}
          micAnalyser={micAnalyserRef.current}
          outputAnalyser={outputAnalyser}
          speaking={speaking}
          muted={muted}
          onToggleMute={toggleMute}
          onStop={stop}
          ttsSpeed={ttsSpeed}
          onCycleSpeed={cycleSpeed}
          idleSeconds={idleSeconds}
          onKeepAlive={keepAlive}
        />,
        bannerSlot,
      );
    }

    // Fallback when no banner slot is wired (standalone use / unit tests):
    // today's inline control row.
    const muteLabel = muted ? UNMUTE_LABEL : MUTE_LABEL;
    const MuteIcon = muted ? MicOff : Mic;
    return (
      <div className="order-last flex w-full items-center justify-center gap-3 rounded-md border bg-background px-2 py-1 text-primary sm:order-none sm:w-auto sm:justify-start sm:gap-2">
        {idleSeconds !== null ? (
          <output className="flex items-center gap-2 text-body-3 text-amber-600">
            <span>Sin actividad — se cerrará en {idleSeconds}s</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="!h-7 !px-2"
              onClick={keepAlive}
            >
              Seguir
            </Button>
          </output>
        ) : null}
        <WaveformVisualizer stream={stream} audioContext={audioContext} className="block" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={`!h-7 !px-1.5 ${muted ? "text-destructive" : ""}`}
          onClick={toggleMute}
          aria-label={muteLabel}
          aria-pressed={muted}
          title={muteLabel}
        >
          <MuteIcon className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="!h-7 !px-1.5"
          onClick={handleClick}
          aria-label={ARIA_LABELS.listening}
          title={ARIA_LABELS.listening}
        >
          <Square className="h-3.5 w-3.5 fill-current" />
        </Button>
      </div>
    );
  }

  const unavailable = sessionId === null;
  const Icon = state === "loading" ? Loader2 : AudioLines;
  const iconClassName = state === "loading" ? "h-5 w-5 animate-spin" : "h-5 w-5";
  const title = unavailable
    ? "No se pudo preparar una sesion de voz"
    : state === "error"
      ? (errorMessage ?? ARIA_LABELS.error)
      : ARIA_LABELS[state];

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="!px-2"
      onClick={handleClick}
      disabled={disabled || unavailable}
      aria-label={unavailable ? ARIA_LABELS.unavailable : ARIA_LABELS[state]}
      title={title}
    >
      <Icon className={iconClassName} />
    </Button>
  );
}

// RMS amplitude (0..1) of an analyser's current time-domain frame. Used both
// by the ambient-accent loop and the echo barge-in gate.
function readAnalyserLevel(analyser: AnalyserNode | null): number {
  if (!analyser) return 0;
  const samples = new Uint8Array(analyser.fftSize);
  analyser.getByteTimeDomainData(samples);
  let sumSquares = 0;
  for (let i = 0; i < samples.length; i++) {
    const centered = (samples[i] ?? 128) - 128;
    sumSquares += centered * centered;
  }
  return Math.sqrt(sumSquares / samples.length) / 128;
}

function pcm16Base64ToAudioBuffer(base64Audio: string, context: AudioContext): AudioBuffer {
  const binary = atob(base64Audio);
  const samples = Math.floor(binary.length / 2);
  const audioBuffer = context.createBuffer(1, samples, OUTPUT_SAMPLE_RATE);
  const channel = audioBuffer.getChannelData(0);
  for (let i = 0; i < samples; i += 1) {
    const lo = binary.charCodeAt(i * 2);
    const hi = binary.charCodeAt(i * 2 + 1);
    const value = (hi << 8) | lo;
    const signed = value >= 0x8000 ? value - 0x10000 : value;
    channel[i] = signed / 0x8000;
  }
  return audioBuffer;
}
